import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { ReleaseNotesService } from '../../services/master';
import { TranslateService } from '@ngx-translate/core';
import { HasPermissionService } from 'src/app/services/common/has-permission.service';

@Component({
  standalone: false,
  selector: 'app-whats-new-list',
  templateUrl: './whats-new-list.component.html',
  styleUrls: ['./whats-new-list.component.scss']
})
export class WhatsNewListComponent implements OnInit, OnDestroy {
  public activeReleaseNotes: any[] = [];
  public inactiveReleaseNotes: any[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    private translate: TranslateService,
    private releseNoteService: ReleaseNotesService,
    private datepipe: DatePipe,
    private router: Router,
    private permission: HasPermissionService,
  ) {}

  ngOnInit(): void {
    this.getAllReleaseNotes();
  }

  getAllReleaseNotes() {
    this.activeReleaseNotes = [];
    this.inactiveReleaseNotes = [];

    this.releseNoteService.apiReleaseNotesGetAllReleaseNotesGet().subscribe((data) => {
      const today = new Date();

      data.forEach((item: any) => {
        const parsedNotes = this.parseReleaseNotes(item.releaseNote); // -> string[]
        const parsedInitialDate = this.parseDate(item.initialDate);
        const endDate = new Date(item.endDate);

        const note = {
          ...item,
          releaseNote: parsedNotes.ReleaseNotes, // normalized string[]
          initialDate: parsedInitialDate
        };

        if (item.isActive && today <= endDate) {
          this.activeReleaseNotes.push(note);
        } else {
          this.inactiveReleaseNotes.push(note);
        }
      });
    });
  }

  parseDate(date: string): string {
    try {
      return this.datepipe.transform(new Date(date), 'd MMM y') || '';
    } catch {
      return '';
    }
  }

  /**
   * Normalize to { ReleaseNotes: string[] }
   * - Supports legacy: string[]
   * - Supports new: {text, isHeading}[]  -> converts to "<strong>…</strong>" when isHeading
   * We keep strings as-is (may already contain <strong>…</strong>).
   */
  parseReleaseNotes(jsonString: string): { ReleaseNotes: string[] } {
    try {
      const parsed = JSON.parse(jsonString);
      const arr = Array.isArray(parsed?.ReleaseNotes) ? parsed.ReleaseNotes : [];

      const normalized: string[] = arr.map((item: any) => {
        if (item && typeof item === 'object' && 'text' in item) {
          const txt = String(item.text ?? '');
          return item.isHeading ? `<strong>${this.escapeHtml(txt)}</strong>` : this.escapeHtml(txt);
        }
        if (typeof item === 'string') {
          // keep as-is (Angular sanitizes in [innerHTML])
          return item;
        }
        return '';
      });

      return { ReleaseNotes: normalized };
    } catch {
      return { ReleaseNotes: [] };
    }
  }

  /** Minimal escaper for object->string conversion */
  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** True if the normalized string is a heading (wrapped in <strong>…</strong>) */
  isHeadingHtml(s: string): boolean {
    return /^<strong>.*<\/strong>$/i.test(s.trim());
  }

  openAddReleaseNote() {
    this.router.navigate(['/whats-new-add']);
  }

  goBackToWhatsNew(): void {
    this.router.navigate(['/whats-new']);
  }

  editNote(releaseNote: any): void {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(releaseNote))));
    this.router.navigate(['/whats-new-edit'], { queryParams: { data: encoded } });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}